const CUSTOM_CAROUSEL_SELECTOR = '[data-custom-product-carousel], [data-custom-blog-posts], [data-custom-blog-related]';

/**
 * @returns {boolean}
 */
function isCartDrawerOpen() {
  const drawer = document.getElementById('cart-drawer');
  return drawer instanceof HTMLElement && drawer.hasAttribute('open');
}

/**
 * @param {HTMLElement} element
 * @returns {{
 *   swiperSelector: string;
 *   navSelector: string;
 *   nextSelector: string;
 *   prevSelector: string;
 * }}
 */
function getCustomCarouselSelectors(element) {
  if (element.hasAttribute('data-custom-blog-related')) {
    return {
      swiperSelector: '.custom-blog-post-related__swiper',
      navSelector: '.custom-blog-post-related__nav',
      nextSelector: '.custom-blog-post-related__arrow--next',
      prevSelector: '.custom-blog-post-related__arrow--prev',
    };
  }

  if (element.hasAttribute('data-custom-blog-posts')) {
    return {
      swiperSelector: '.custom-blog-posts__swiper',
      navSelector: '.custom-blog-posts__nav',
      nextSelector: '.custom-blog-posts__arrow--next',
      prevSelector: '.custom-blog-posts__arrow--prev',
    };
  }

  return {
    swiperSelector: '.custom-product-carousel__swiper',
    navSelector: '.custom-product-carousel__nav',
    nextSelector: '.custom-product-carousel__arrow--next',
    prevSelector: '.custom-product-carousel__arrow--prev',
  };
}

/**
 * @param {HTMLElement} element
 * @returns {Record<string, unknown>}
 */
function buildProductCarouselConfig(element) {
  const slidesMobile = Number(element.dataset.slidesMobile) || 1;
  const slidesTablet = Number(element.dataset.slidesTablet) || 2;
  const slidesDesktop = Number(element.dataset.slidesDesktop) || 3;
  const slidesDesktopWide = Number(element.dataset.slidesDesktopWide) || 4;
  const spaceMobile = Number(element.dataset.spaceMobile) || 12;
  const spaceDesktop = Number(element.dataset.spaceDesktop) || 16;
  const breakpointTablet = Number(element.dataset.breakpointTablet) || 768;
  const breakpointDesktop = Number(element.dataset.breakpointDesktop) || 1024;
  const breakpointDesktopWide = Number(element.dataset.breakpointDesktopWide) || 1366;

  /** @type {Record<number, { slidesPerView: number; slidesPerGroup: number; spaceBetween: number }>} */
  const breakpoints = {};

  breakpoints[breakpointTablet] = {
    slidesPerView: slidesTablet,
    slidesPerGroup: 1,
    spaceBetween: spaceMobile,
  };

  breakpoints[breakpointDesktop] = {
    slidesPerView: slidesDesktop,
    slidesPerGroup: 1,
    spaceBetween: spaceDesktop,
  };

  if (!isCartDrawerOpen()) {
    breakpoints[breakpointDesktopWide] = {
      slidesPerView: slidesDesktopWide,
      slidesPerGroup: 1,
      spaceBetween: spaceDesktop,
    };
  }

  /** @type {Record<string, unknown>} */
  const config = {
    speed: 400,
    loop: false,
    watchOverflow: true,
    grabCursor: true,
    preloadImages: false,
    slidesPerGroup: 1,
    slidesPerView: slidesMobile,
    spaceBetween: spaceMobile,
    breakpoints,
    a11y: {
      enabled: true,
    },
  };

  if (element.dataset.pagination === 'true') {
    config.autoHeight = true;
    config.observer = true;
    config.observeParents = true;
  }

  if (element.dataset.autoplay === 'true') {
    config.autoplay = {
      delay: (Number(element.dataset.autoplayDelay) || 5) * 1000,
      disableOnInteraction: false,
      pauseOnMouseEnter: true,
    };
  }

  const selectors = getCustomCarouselSelectors(element);
  const paginationElement = element.querySelector('.swiper-pagination');

  if (element.dataset.pagination === 'true' && paginationElement instanceof HTMLElement) {
    config.pagination = {
      el: paginationElement,
      clickable: true,
    };
  }

  const nextElement = element.querySelector(selectors.nextSelector);
  const prevElement = element.querySelector(selectors.prevSelector);
  if (
    element.dataset.arrows === 'true' &&
    nextElement instanceof HTMLElement &&
    prevElement instanceof HTMLElement
  ) {
    config.navigation = {
      nextEl: nextElement,
      prevEl: prevElement,
    };
  }

  return config;
}

/**
 * @param {HTMLElement} element
 */
function updateProductCarouselNav(element) {
  const selectors = getCustomCarouselSelectors(element);
  const nav = element.querySelector(selectors.navSelector);
  if (!(nav instanceof HTMLElement)) return;

  const swiper = element.swiperInstance;
  if (!swiper) {
    nav.hidden = true;
    return;
  }

  nav.hidden = Boolean(swiper.isLocked);
}

/**
 * @param {HTMLElement} element
 */
function destroyProductCarousel(element) {
  if (element.swiperInstance && typeof element.swiperInstance.destroy === 'function') {
    element.swiperInstance.destroy(true, true);
    element.swiperInstance = null;
  }
}

function refreshAllProductCarousels() {
  document.querySelectorAll(CUSTOM_CAROUSEL_SELECTOR).forEach(function (element) {
    if (!(element instanceof HTMLElement)) return;
    if (element.dataset.initialized === 'true') {
      destroyProductCarousel(element);
      delete element.dataset.initialized;
    }
  });
  initProductCarousels(document);
}

/**
 * @param {Element | Document} root
 */
function initProductCarousels(root) {
  if (!(root instanceof Element || root instanceof Document)) return;
  if (typeof window.Swiper !== 'function') return;

  root.querySelectorAll(CUSTOM_CAROUSEL_SELECTOR).forEach(function (element) {
    if (!(element instanceof HTMLElement)) return;

    const selectors = getCustomCarouselSelectors(element);
    const swiperElement = element.querySelector(selectors.swiperSelector);
    if (!(swiperElement instanceof HTMLElement)) return;

    if (element.dataset.initialized === 'true') {
      destroyProductCarousel(element);
    }

    const SwiperCtor = window.Swiper;
    if (typeof SwiperCtor !== 'function') return;

    element.swiperInstance = new SwiperCtor(swiperElement, buildProductCarouselConfig(element));
    element.swiperInstance.on('resize', function () {
      updateProductCarouselNav(element);
    });
    element.swiperInstance.on('breakpoint', function () {
      updateProductCarouselNav(element);
    });
    updateProductCarouselNav(element);
    element.dataset.initialized = 'true';
  });
}

/**
 * @param {number} attempt
 */
function bootProductCarousels(attempt) {
  if (typeof window.Swiper === 'function') {
    initProductCarousels(document);
    return;
  }

  if (attempt >= 20) return;

  window.setTimeout(function () {
    bootProductCarousels(attempt + 1);
  }, 100);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function () {
    bootProductCarousels(0);
  });
} else {
  bootProductCarousels(0);
}

document.addEventListener('shopify:section:load', function (event) {
  if (!(event.target instanceof HTMLElement)) return;
  initProductCarousels(event.target);
});

document.addEventListener('shopify:section:unload', function (event) {
  if (!(event.target instanceof HTMLElement)) return;

  event.target.querySelectorAll(CUSTOM_CAROUSEL_SELECTOR).forEach(function (element) {
    if (element instanceof HTMLElement) {
      destroyProductCarousel(element);
      delete element.dataset.initialized;
    }
  });
});

document.addEventListener('theme-drawer:open', function (event) {
  if (!(event.target instanceof HTMLElement)) return;
  if (event.target.id !== 'cart-drawer') return;
  refreshAllProductCarousels();
});

document.addEventListener('theme-drawer:close', function (event) {
  if (!(event.target instanceof HTMLElement)) return;
  if (event.target.id !== 'cart-drawer') return;
  refreshAllProductCarousels();
});
